import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../features/auth/context';
import { safeNextPath } from '../features/auth/nextPath';
import GoogleSignInButton from '../features/auth/GoogleSignInButton';
import Input from '../components/ui/Input';
import FormField from '../components/ui/FormField';
import Checkbox from '../components/ui/Checkbox';
import { useDocumentTitle } from '../shared/useDocumentTitle';

const SignupPage: React.FC = () => {
  useDocumentTitle('Create account');
  const { user } = useAuth();
  const navigate = useNavigate();
  // Same ?next= contract as the login page: a deep link that lands a logged-out
  // visitor here (or bounces through login) keeps its destination. Untrusted
  // input — validated by the same-origin allowlist in features/auth/nextPath.ts.
  const [searchParams] = useSearchParams();
  const destination = safeNextPath(searchParams.get('next')) ?? '/';
  // The "already have an account" link must keep carrying the destination —
  // an existing user arriving from the guest signup wall would otherwise log
  // in and lose the trip/journey they were looking at.
  const loginHref = safeNextPath(searchParams.get('next'))
    ? `/login?next=${encodeURIComponent(safeNextPath(searchParams.get('next')) as string)}`
    : '/login';

  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      navigate(destination, { replace: true });
    }
  }, [user, navigate, destination]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    acceptedTerms: false,
    acceptedPrivacyPolicy: false
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { signup, googleLogin } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (!formData.acceptedTerms || !formData.acceptedPrivacyPolicy) {
      setError('You must agree to continue');
      return;
    }

    setLoading(true);
    try {
      await signup({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        acceptedTerms: formData.acceptedTerms,
        acceptedPrivacyPolicy: formData.acceptedPrivacyPolicy
      });
      navigate(destination, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (credential: string) => {
    setError(null);
    setLoading(true);
    try {
      // Consent is given via the disclosure line under the Google button
      // ("By continuing with Google, you agree to ..."), not the checkboxes.
      await googleLogin({
        credential,
        acceptedTerms: true,
        acceptedPrivacyPolicy: true
      });
      navigate(destination, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Google sign-up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 400, margin: '2rem auto', padding: '0 1rem' }}>
      <p style={{ textAlign: 'center', marginTop: '1rem' }}>
        Already have an account? <a href={loginHref}>Login</a>
      </p>
      <h1>Get started</h1>
      
      {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}

      <GoogleSignInButton
        onCredential={handleGoogleCredential}
        disclosure={
          <>
            By continuing with Google, you agree to Jody's{' '}
            <a
              href="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--green-primary-bg)', textDecoration: 'underline' }}
            >
              Terms and Conditions
            </a>{' '}
            and{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--green-primary-bg)', textDecoration: 'underline' }}
            >
              Privacy Policy
            </a>
            .
          </>
        }
      />

      <form onSubmit={handleSubmit}>
        <FormField label="First Name">
          <Input
            type="text"
            name="firstName"
            placeholder="John"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
        </FormField>

        <FormField label="Last Name">
          <Input
            type="text"
            name="lastName"
            placeholder="Smith"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </FormField>
        
        <FormField label="Email">
          <Input
            type="email"
            name="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </FormField>
        
        <FormField label="Password">
          <Input
            type="password"
            name="password"
            placeholder="Your secret password"
            value={formData.password}
            onChange={handleChange}
            required
          />
        </FormField>

        <div style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <Checkbox
              name="acceptedTerms"
              checked={formData.acceptedTerms}
              onChange={handleChange}
              label={
                <>
                  I agree to Jody's{' '}
                  <a
                    href="/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--green-primary-bg)', textDecoration: 'underline' }}
                  >
                    Terms and Conditions
                  </a>
                </>
              }
            />
          </div>
          <div>
            <Checkbox
              name="acceptedPrivacyPolicy"
              checked={formData.acceptedPrivacyPolicy}
              onChange={handleChange}
              label={
                <>
                  I have read and understand Jody's{' '}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--green-primary-bg)', textDecoration: 'underline' }}
                  >
                    Privacy Policy
                  </a>
                </>
              }
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !formData.acceptedTerms || !formData.acceptedPrivacyPolicy}
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: (loading || !formData.acceptedTerms || !formData.acceptedPrivacyPolicy) ? '#ccc' : 'var(--green-primary-bg)',
            color: 'var(--green-primary-fg)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: (loading || !formData.acceptedTerms || !formData.acceptedPrivacyPolicy) ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Getting started...' : 'Get started'}
        </button>
      </form>
    </div>
  );
};

export default SignupPage; 